
import { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: Date;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

export default function PromoCountdown({ targetDate, label = 'Deal Ends In', size = 'md' }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;
      if (distance <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hrs', value: timeLeft.hours },
    { label: 'Min', value: timeLeft.minutes },
    { label: 'Sec', value: timeLeft.seconds },
  ];

  const sizeClasses = {
    sm: { box: 'w-12 h-12', num: 'text-lg', lbl: 'text-[10px]', gap: 'gap-2' },
    md: { box: 'w-16 h-16', num: 'text-2xl', lbl: 'text-[10px]', gap: 'gap-3' },
    lg: { box: 'w-20 h-20 md:w-24 md:h-24', num: 'text-3xl md:text-4xl', lbl: 'text-xs', gap: 'gap-4' },
  };

  const s = sizeClasses[size];

  return (
    <div className="text-center">
      {label && (
        <p className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-3">{label}</p>
      )}
      <div className={`flex items-center justify-center ${s.gap}`}>
        {units.map((unit, i) => (
          <div key={unit.label} className="flex items-center gap-2">
            <div className={`${s.box} bg-white/10 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center border border-white/10`}>
              <span className={`${s.num} font-bold text-white font-display leading-none`}>
                {String(unit.value).padStart(2, '0')}
              </span>
              <span className={`${s.lbl} text-slate-400 uppercase tracking-wider mt-0.5`}>{unit.label}</span>
            </div>
            {i < units.length - 1 && (
              <span className={`${s.num} text-amber-500 font-bold`}>:</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
